import { getCurrentLanguage, interpolate } from '../translations.js';

export const PROVIDER_CREDIT_MULTIPLIERS = {
  elevenlabs: 1,
  xai: 0.15,
};

/**
 * @param {string | null | undefined} provider
 * @returns {number}
 */
export const getProviderCreditMultiplier = (provider) => (
  PROVIDER_CREDIT_MULTIPLIERS[provider] ?? 1
);

/**
 * @param {number | string | null | undefined} input
 * @returns {number}
 */
export const parseCreditMultiplier = (input) => {
  if (input == null || input === '') return 1;
  if (typeof input === 'number') return input;

  const str = String(input).trim().toLowerCase();
  if (str === 'free') return 1;

  const cleaned = str.replace(/x$/i, '');
  const parsed = parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 1;
};

/**
 * @param {{ provider?: string | null, multiplier?: number | string | null }} voice
 * @returns {number}
 */
export const getEffectiveVoiceMultiplier = (voice) => {
  const providerMultiplier = getProviderCreditMultiplier(voice?.provider);
  const voiceMultiplier = parseCreditMultiplier(voice?.multiplier);
  return providerMultiplier * voiceMultiplier;
};

/**
 * @param {number} multiplier
 * @returns {string}
 */
export const formatCreditMultiplier = (multiplier) => {
  const display = Number(multiplier) === Math.floor(multiplier)
    ? String(multiplier)
    : String(Number(multiplier.toFixed(2)));
  return `${display}×`;
};

/**
 * @param {number} multiplier
 * @param {'chat' | 'voice'} [variant='chat']
 * @returns {string}
 */
export const getCreditMultiplierTooltip = (multiplier, variant = 'chat') => {
  const lang = getCurrentLanguage();
  const display = Number(multiplier) === Math.floor(multiplier)
    ? String(multiplier)
    : String(Number(multiplier.toFixed(2)));

  const key = variant === 'voice'
    ? 'voice_credit_multiplier_tooltip'
    : 'credit_multiplier_tooltip';

  const fallback = variant === 'voice'
    ? 'Uses {multiplier}× voice credits'
    : 'Uses {multiplier}× chat credits';

  const template = window.translations?.[lang]?.hangout?.character_modal?.[key]
    ?? window.translations?.en?.hangout?.character_modal?.[key]
    ?? fallback;

  return interpolate(template, { multiplier: display });
};

/**
 * @param {number | string | null | undefined} input
 * @param {{ hideWhenOne?: boolean, variant?: 'chat' | 'voice' }} [options]
 * @returns {HTMLSpanElement | null}
 */
export const createCreditMultiplierBadge = (input, { hideWhenOne = false, variant = 'chat' } = {}) => {
  const multiplier = parseCreditMultiplier(input);
  if (hideWhenOne && multiplier === 1) return null;

  const badge = document.createElement('span');
  badge.className = variant === 'voice'
    ? 'credit-multiplier credit-multiplier-badge'
    : 'credit-multiplier vibe-tag credit-multiplier-tag';

  if (multiplier < 1) badge.classList.add('is-cheaper');
  else if (multiplier > 1) badge.classList.add('is-premium');

  badge.textContent = formatCreditMultiplier(multiplier);
  badge.dataset.multiplier = String(multiplier);

  const tooltip = getCreditMultiplierTooltip(multiplier, variant);
  badge.title = tooltip;
  badge.setAttribute('aria-label', tooltip);

  return badge;
};
